import React from 'react';
import { Modal, Button, Typography, Space, Card, Row, Col, Tag, Divider } from 'antd';
import {
    StarOutlined,
    CheckOutlined,
    LockOutlined,
    RocketOutlined
} from '@ant-design/icons';
import { SubscriptionPlanEnum } from '../services/nestjs-base-api/types';
import { useGetAnnualPromotionQuery } from '../services/nestjs-base-api';
import { useAuth } from '../hooks/useAuth';

const { Title, Text, Paragraph } = Typography;

interface ISubscriptionUpgradeModalProps {
    visible: boolean;
    onCancel: () => void;
    onUpgrade: (plan: SubscriptionPlanEnum) => void;
    currentPlan?: SubscriptionPlanEnum | null;
    requiredPlan: SubscriptionPlanEnum;
    featureName: string;
    brandName?: string;
    loading?: boolean;
}

const SubscriptionUpgradeModal: React.FunctionComponent<ISubscriptionUpgradeModalProps> = ({
    visible,
    onCancel,
    onUpgrade,
    currentPlan,
    requiredPlan,
    featureName,
    brandName,
    loading = false,
}) => {
    const { user } = useAuth();

    // Fetch annual promotion to calculate actual savings
    const { data: annualPromotion } = useGetAnnualPromotionQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !user?.jwtToken }
    );

    // Get prices from system settings via API
    const monthlyPrice = annualPromotion?.pricing?.monthlyPrice || 990;
    const yearlyOriginalPrice = annualPromotion?.pricing?.yearlyPrice || 9900;
    const hasAnnualDiscount = annualPromotion?.active && annualPromotion.discountPercent > 0;
    const couponDiscountPercent = annualPromotion?.discountPercent || 0;
    const yearlyDiscountedPrice = hasAnnualDiscount
        ? Math.round(yearlyOriginalPrice * (1 - couponDiscountPercent / 100))
        : yearlyOriginalPrice;

    // Calculate actual savings vs paying monthly for a year
    const yearlyEquivalentOfMonthly = monthlyPrice * 12;
    const actualSavingsAmount = yearlyEquivalentOfMonthly - yearlyDiscountedPrice;
    const actualSavingsPercent = Math.round((actualSavingsAmount / yearlyEquivalentOfMonthly) * 100);

    // Standard yearly savings (without promotional coupon)
    const standardYearlySavings = yearlyEquivalentOfMonthly - yearlyOriginalPrice;
    const standardSavingsPercent = Math.round((standardYearlySavings / yearlyEquivalentOfMonthly) * 100);

    const professionalFeatures = [
        'Full BE model access',
        'Product type benchmarking',
        'AI Experts (20 credits/month)',
        'Advanced analytics',
        'Unlimited responses',
        'Full API access',
        'Priority support',
    ];

    const getPlanPrice = (plan: SubscriptionPlanEnum, cycle: 'monthly' | 'yearly') => {
        if (plan === SubscriptionPlanEnum.Professional) {
            return cycle === 'monthly' ? monthlyPrice : yearlyOriginalPrice;
        } else {
            // Enterprise prices - could also be fetched from API in the future
            return cycle === 'monthly' ? 1500 : 15000;
        }
    };

    const renderPlanCard = (
        plan: SubscriptionPlanEnum, 
        features: string[], 
        icon: React.ReactNode,
        isRecommended: boolean = false,
        isCurrentPlan: boolean = false
    ) => (
        <Card
            title={
                <Space>
                    {icon}
                    <Text strong>{plan}</Text>
                    {isRecommended && <Tag color="gold">Recommended</Tag>}
                    {isCurrentPlan && <Tag color="green">Current Plan</Tag>}
                </Space>
            }
            style={{ 
                height: '100%',
                border: isRecommended ? '2px solid #1890ff' : undefined,
                position: 'relative'
            }}
            actions={[
                isCurrentPlan ? (
                    <Text type="secondary">Current Plan</Text>
                ) : (
                    <Button
                        type={isRecommended ? 'primary' : 'default'}
                        block
                        onClick={() => onUpgrade(plan)}
                        loading={loading}
                        icon={<RocketOutlined />}
                        style={{ width: '85%', margin: '0 16px' }}
                    >
                        {currentPlan ? 'Upgrade to' : 'Subscribe to'} {plan}
                    </Button>
                )
            ]}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                    <Title level={3} style={{ margin: 0 }}>
                        ${getPlanPrice(plan, 'monthly')}/mo
                    </Title>
                    {plan === SubscriptionPlanEnum.Professional && hasAnnualDiscount ? (
                        <div>
                            <Text type="secondary" style={{ textDecoration: 'line-through', marginRight: 8 }}>
                                ${yearlyOriginalPrice.toLocaleString()}
                            </Text>
                            <Text type="secondary">
                                ${yearlyDiscountedPrice.toLocaleString()}/year
                            </Text>
                            <Tag color="green" style={{ marginLeft: 8 }}>{actualSavingsPercent}% OFF - Limited Time!</Tag>
                            <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                    First year only, then ${yearlyOriginalPrice.toLocaleString()}/yr
                                </Text>
                            </div>
                        </div>
                    ) : (
                        <Text type="secondary">or ${getPlanPrice(plan, 'yearly').toLocaleString()}/year (Save {standardSavingsPercent}%)</Text>
                    )}
                </div>
                
                <Divider />
                
                <div>
                    <Title level={5}>Features Included:</Title>
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {features.map((feature, index) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                                <CheckOutlined style={{ color: '#52c41a', marginRight: 8, marginTop: 4 }} />
                                <Text>{feature}</Text>
                            </div>
                        ))}
                    </Space>
                </div>
            </Space>
        </Card>
    );

    const getUpgradeMessage = () => {
        if (!currentPlan) {
            return `To access ${featureName}${brandName ? ` for ${brandName}` : ''}, you need an active subscription.`;
        }

        return `To access ${featureName}${brandName ? ` for ${brandName}` : ''}, you need a Professional subscription.`;
    };

    return (
        <Modal
            title={
                <Space>
                    <LockOutlined style={{ color: '#faad14' }} />
                    <Text>Feature Upgrade Required</Text>
                </Space>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={900}
            style={{ top: 20 }}
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Title level={3}>{featureName} requires a subscription upgrade</Title>
                    <Paragraph style={{ fontSize: 16, marginBottom: 0 }}>
                        {getUpgradeMessage()}
                    </Paragraph>
                </div>

                <Row gutter={[24, 24]} justify="center">
                    <Col xs={24} lg={16}>
                        {renderPlanCard(
                            SubscriptionPlanEnum.Professional,
                            professionalFeatures,
                            <StarOutlined style={{ color: '#1890ff' }} />,
                            true,
                            currentPlan === SubscriptionPlanEnum.Professional
                        )}
                    </Col>
                </Row>

                <div style={{ textAlign: 'center', marginTop: 24 }}>
                    <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
                        <Button size="large" onClick={onCancel} style={{ minWidth: 120 }}>
                            Maybe Later
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => onUpgrade(SubscriptionPlanEnum.Professional)}
                            loading={loading}
                            icon={<RocketOutlined />}
                            style={{ minWidth: 200 }}
                        >
                            Upgrade to Professional
                        </Button>
                    </Space>
                </div>

                <div style={{
                    background: '#f9f9f9',
                    padding: 16,
                    borderRadius: 8,
                    marginTop: 24,
                    textAlign: 'center'
                }}>
                    <Text type="secondary">
                        No setup fees • Instant access
                    </Text>
                </div>
            </Space>
        </Modal>
    );
};

export default SubscriptionUpgradeModal;